import { Toast } from '@byecode/ui/components/Toast'
import { nanoid } from '@lighthouse/tools'
import type { UploadOptions } from '@rpldy/uploady'
import type { Node as PMNode } from '@tiptap/pm/model'
import { Slice } from '@tiptap/pm/model'
import type { Transaction } from '@tiptap/pm/state'
import type { PluginKey } from '@tiptap/pm/state'
import type { EditorView } from '@tiptap/pm/view'
import type { DecorationSet } from '@tiptap/pm/view'
import { t } from 'i18next'

import { tokenStorage } from '../../../utils/token'
import type { TokenRefreshManager } from '../../../utils/tokenRefreshManager'
import { formatFileSize, webImg2File } from './helpers'
import type { PlaceholderMeta, SliceSlot, UploadingItem } from './types'
import { isPlaceholderMeta } from './types'

export class UploadImageManager {
    readonly #uploadOptions?: UploadOptions

    readonly #tokenRefreshManager?: TokenRefreshManager

    readonly #maxSize: number

    readonly #pluginKey: PluginKey<DecorationSet>

    #placeholderPosById = new Map<string, number>()
    #nextInsertPosByBatchId = new Map<string, number>()

    constructor(
        uploadOptions: UploadOptions | undefined,
        tokenRefreshManager: TokenRefreshManager | undefined,
        maxSize: number,
        pluginKey: PluginKey<DecorationSet>
    ) {
        this.#uploadOptions = uploadOptions
        this.#tokenRefreshManager = tokenRefreshManager
        this.#maxSize = maxSize
        this.#pluginKey = pluginKey
    }

    findPlaceholder(view: EditorView, id: string): number {
        const decoSet = this.#pluginKey.getState(view.state)
        if (decoSet) {
            const match = decoSet.find(undefined, undefined, (spec: { id?: string }) => spec.id === id)
            if (match.length > 0) {
                const pos = match[0].from
                this.#placeholderPosById.set(id, pos)
                return pos
            }
        }

        const trackedPos = this.#placeholderPosById.get(id)
        return trackedPos ?? -1
    }

    removePlaceholder(view: EditorView, id: string) {
        this.#placeholderPosById.delete(id)
        const { tr } = view.state
        tr.setMeta(this.#pluginKey, { type: 'remove', id })
        view.dispatch(tr)
    }

    syncPlaceholderPositionMap(tr: Transaction, meta: unknown) {
        const mappedPositions = new Map<string, number>()
        this.#placeholderPosById.forEach((pos, id) => {
            const mappedPos = tr.mapping.map(pos, 1)
            mappedPositions.set(id, Math.min(mappedPos, tr.doc.content.size))
        })
        this.#placeholderPosById = mappedPositions

        if (!isPlaceholderMeta(meta)) {
            return
        }

        if (meta.type === 'add') {
            this.#placeholderPosById.set(meta.id, Math.min(meta.pos, tr.doc.content.size))
            return
        }

        if (meta.type === 'addMany') {
            meta.items.forEach(item => {
                this.#placeholderPosById.set(item.id, Math.min(item.pos, tr.doc.content.size))
            })
            return
        }

        this.#placeholderPosById.delete(meta.id)
    }

    resolveImageInsertRange(doc: PMNode, rawPos: number): { from: number; to: number } {
        const clampedPos = Math.max(0, Math.min(rawPos, doc.content.size))
        const $pos = doc.resolve(clampedPos)
        if ($pos.depth > 0 && $pos.parent.isTextblock) {
            if ($pos.parentOffset === 0) {
                const boundaryPos = $pos.before()
                return { from: boundaryPos, to: boundaryPos }
            }
            if ($pos.parentOffset === $pos.parent.content.size) {
                const boundaryPos = $pos.after()
                return { from: boundaryPos, to: boundaryPos }
            }
        }
        if ($pos.depth > 0 && $pos.parent.isTextblock && $pos.parent.content.size === 0) {
            return {
                from: $pos.before(),
                to: $pos.after()
            }
        }
        if ($pos.nodeAfter?.isTextblock && $pos.nodeAfter.content.size === 0) {
            return {
                from: clampedPos,
                to: clampedPos + $pos.nodeAfter.nodeSize
            }
        }
        if ($pos.nodeBefore?.isTextblock && $pos.nodeBefore.content.size === 0) {
            return {
                from: clampedPos - $pos.nodeBefore.nodeSize,
                to: clampedPos
            }
        }
        return { from: clampedPos, to: clampedPos }
    }

    /**
     * 多图上传的完整稳定策略（本次问题修复结论）
     *
     * 目标（看起来简单，但在 PM 下必须显式约束）：
     * 1) 顺序稳定：按 [a,b,c] -> [a,b,c]
     * 2) 位置稳定：都插在用户原始锚点附近，不漂移到文档尾部或后继 block
     * 3) 结构稳定：不引入额外空段落，不把图片挤进下一节点（例如 blockquote）
     *
     * 为什么不是简单 “pos++”：
     * - ProseMirror 的 pos 是树结构位置，不是数组下标；
     * - 每次 dispatch 后，位置必须通过 tr.mapping 重新映射；
     * - Decoration placeholder 在复杂事务里可能被映射到边界（如 doc 尾部）；
     * - block image 插入 textblock 内部会触发切段，不处理会产生空行。
     *
     * 当前实现分三层约束：
     * A. 批次锚点（#nextInsertPosByBatchId）
     *    - 第一张使用初始 pos；
     *    - 后续图片优先使用批次锚点，避免依赖后续 placeholder 的漂移位置。
     *
     * B. 插入范围归一化（resolveImageInsertRange）
     *    - 命中 textblock 起止边界时折叠到块间边界；
     *    - 命中空 textblock 时直接 replace 整段空块；
     *    - 命中块边界且相邻空 textblock 时直接 replace 相邻空块；
     *    - 其余场景走普通 insert。
     *
     * C. 锚点递进（每次上传成功后）
     *    - 下一锚点使用 tr.mapping.map(to, 1)；
     *    - 即“本次插入区间在新文档中的尾部”，保证下一张接在当前图后侧；
     *    - 可避免顺序反转、跳入后继节点、尾部场景错位等问题。
     *
     * 结论：
     * - 抽象上仍是“串行上传 + 更新最新 pos”；
     * - 只是这个最新 pos 必须来自事务 mapping，并配合范围归一化，才能在 PM 里稳定成立。
     */
    async uploadInSequence(view: EditorView, items: UploadingItem[]) {
        for (const item of items) {
            await this.upload(view, item, item.fallbackPos)
        }
        if (items[0]?.batchId) {
            this.#nextInsertPosByBatchId.delete(items[0].batchId)
        }
    }

    async upload(view: EditorView, item: UploadingItem, savedPos?: number, isRetry = false) {
        const { destination } = this.#uploadOptions ?? {}

        if (!destination || !destination.url) {
            return
        }

        const file = item.file instanceof File ? item.file : await webImg2File(item.file)
        let pos = this.findPlaceholder(view, item.id)

        if (pos === -1) {
            if (savedPos !== undefined) {
                pos = Math.min(savedPos, view.state.doc.content.size)
            } else {
                return
            }
        }
        if (file.size > this.#maxSize) {
            Toast.error(`${t('fileExceedsUploadLimit', { v: formatFileSize(this.#maxSize) })}`)
            this.removePlaceholder(view, item.id)
            return
        }

        const formData = new FormData()
        formData.append(destination.filesParamName ?? 'file', file)
        if (destination.params) {
            Object.entries(destination.params).forEach(([key, value]) => {
                if (typeof value === 'string') {
                    formData.append(key, value)
                }
            })
        }

        const headers: Record<string, string> = {}
        if (destination.headers) {
            Object.entries(destination.headers as Record<string, string>).forEach(([key, value]) => {
                if (key.toLowerCase() !== 'content-type') {
                    headers[key] = value
                }
            })
        }

        const token = tokenStorage.getAccessToken()
        if (token) {
            headers['Authorization'] = `Bearer ${token}`
        }

        try {
            const res = await fetch(destination.url, { method: 'POST', body: formData, headers })

            if (res.status === 401) {
                if (isRetry) {
                    this.removePlaceholder(view, item.id)
                    throw new Error('重试后仍然收到 401 错误')
                }

                if (!this.#tokenRefreshManager) {
                    this.removePlaceholder(view, item.id)
                    return
                }

                try {
                    const currentPos = pos
                    await this.#tokenRefreshManager.handleUpload401(item.id, async () => {
                        await this.upload(view, item, currentPos, true)
                    })
                } catch {
                    this.removePlaceholder(view, item.id)
                }

                return
            }

            const data = await res.json()
            const { url } = data.content

            const imageNode = view.state.schema.nodes.image.create({ src: url })
            const { tr, doc } = view.state

            const currentPlaceholderPos = this.findPlaceholder(view, item.id)
            const batchInsertPos =
                item.insertStrategy === 'batch-anchor' && item.batchId ? this.#nextInsertPosByBatchId.get(item.batchId) : undefined
            const preferredPos = currentPlaceholderPos === -1 ? batchInsertPos ?? pos : currentPlaceholderPos
            const insertPos = Math.min(preferredPos, doc.content.size)
            const { from, to } = this.resolveImageInsertRange(doc, insertPos)
            if (from === to) {
                tr.insert(from, imageNode)
            } else {
                tr.replaceWith(from, to, imageNode)
            }
            if (item.insertStrategy === 'batch-anchor' && item.batchId) {
                const mappedRangeEnd = tr.mapping.map(to, 1)
                const nextBatchInsertPos = Math.min(mappedRangeEnd, tr.doc.content.size)
                this.#nextInsertPosByBatchId.set(item.batchId, nextBatchInsertPos)
            }

            if (currentPlaceholderPos !== -1) {
                tr.setMeta(this.#pluginKey, { type: 'remove', id: item.id })
            } else {
                this.#placeholderPosById.delete(item.id)
            }
            view.dispatch(tr)
        } catch (error) {
            this.removePlaceholder(view, item.id)
            if (isRetry) {
                throw error
            }
        }
    }

    /** 使用 Decoration 添加占位符并开始上传（drop、paste 文件）- 不修改 document */
    createDecorationPlaceholdersAndUpload(view: EditorView, files: (File | string)[], pos: number) {
        const batchId = nanoid()
        this.#nextInsertPosByBatchId.set(batchId, pos)
        const items: UploadingItem[] = files.map((file, index) => ({
            id: nanoid(),
            file,
            fallbackPos: pos,
            batchId,
            batchIndex: index,
            insertStrategy: 'batch-anchor'
        }))
        const addMany: PlaceholderMeta = {
            type: 'addMany',
            items: items.map((item, index) => ({ id: item.id, pos, side: index + 1 }))
        }
        const { tr } = view.state
        tr.setMeta(this.#pluginKey, addMany)
        view.dispatch(tr)
        void this.uploadInSequence(view, items)
    }

    /**
     * HTML 粘贴：插入无图 slice，在 slot 位置加 Decoration 并开始上传，上传完成后插入 image 节点并移除 Decoration
     */
    pasteHtmlWithDecorationPlaceholders(view: EditorView, newSlice: Slice, slots: SliceSlot[], insertFrom: number) {
        if (slots.length === 0) {
            return
        }
        const batchId = nanoid()
        const items: UploadingItem[] = slots.map((slot, index) => ({
            id: nanoid(),
            file: slot.file,
            fallbackPos: insertFrom + slot.offset,
            batchId,
            batchIndex: index,
            insertStrategy: 'placeholder'
        }))
        const addMany: PlaceholderMeta = {
            type: 'addMany',
            items: items.map((item, i) => ({ id: item.id, pos: insertFrom + slots[i].offset, side: i + 1 }))
        }
        const tr = view.state.tr.replaceSelection(newSlice)
        tr.setMeta(this.#pluginKey, addMany)
        view.dispatch(tr)
        void this.uploadInSequence(view, items)
    }
}
